import type { MouseEventHandler, ReactNode, Ref } from "react";
import Link from "next/link";
import { cx } from "./fortschritt/breite";

/**
 * Knopf: Schaltfläche oder Link in Knopfgestalt, nur in den OFFIZIELLEN Preline-Formen (Welle 0/1/2, 10.10.2026).
 * Varianten und Größen sind feste, ganze Klassenketten (nie zusammengesetzte Klassennamen, R8); keine freie `className`,
 * kein asChild. `href` rendert next/link (extern: `<a target="_blank" rel="noopener noreferrer">`).
 * Ein Fokus für alle: `focus:outline-hidden` + Hintergrund bzw. Textfarbe `-focus` (Preline), nie Rand+Ring.
 * Betreiber 10.10.2026 („Empfehlung zu allen“): Primär `font-medium`, Ghost-Symbolknöpfe in Preline-Maßen, px-2.5/XS auf
 * Preline-Maß, Größe `lg` + `volleBreite`, Spinner bei `laedt`, Variante `text`.
 * Welle 2b (Koordinator-Entscheid, technisch, ohne sichtbare Änderung): `ref` (React 19, Prop), `daten` (nur `data-*`),
 * `ariaExpanded`/`ariaControls`/`ariaHaspopup`/`ariaPressed`/`ariaDescribedby`, `nurSymbol` darf `href` haben (Symbol-Link).
 * Weiter keine `className`, kein `style`, kein asChild; Klassen nur als ganze Ketten.
 */
export type KnopfVariante = "sekundaer" | "primaer" | "gefahr" | "ghost" | "text";
// `mobil-gross` (additiv, Besprechungen P5-e, Abweichung AW-045 (10)): Touch-Ziel unter `lg` (>= 44 px: Text 46 px, Symbol 44 px),
// ab `lg` genau `md` (Desktop unverändert). Nur für Flächen mit eigener Mobil-Fassung.
// `touch` (additiv, Kantine Warenkorb M-107, Betreiber 19.10.2026 „T-B ok“, Abweichung AW-120): Touch-Ziel in JEDER Breite (>= 44 px:
// Text 44 px, Symbol 44 px); für das Terminal (Tablet quer, ab `lg`), wo `mobil-gross` schon auf Desktop-Maß zurückfällt.
export type KnopfGroesse = "xs" | "sm" | "md" | "lg" | "mobil-gross" | "touch";

// Farbketten: Preline „White“ (buttons-white-color-variants), „Solid“ (buttons-types), Gefahr = White mit Text-Rolle destructive,
// „Ghost“ (buttons-ghost-color-variants / Pro ghost, ghost-icon), „Link“ (buttons-link-color-variants).
const FARBE: Record<KnopfVariante, string> = {
  sekundaer:
    "border border-layer-line bg-layer text-layer-foreground shadow-2xs hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus disabled:opacity-50 disabled:pointer-events-none",
  primaer:
    "bg-primary border border-primary-line text-primary-foreground hover:bg-primary-hover focus:outline-hidden focus:bg-primary-focus disabled:opacity-50 disabled:pointer-events-none",
  gefahr:
    "border border-layer-line bg-layer text-destructive shadow-2xs hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus disabled:opacity-50 disabled:pointer-events-none",
  ghost:
    "border border-transparent text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus disabled:opacity-50 disabled:pointer-events-none",
  text: "text-primary hover:text-primary-hover focus:outline-hidden focus:text-primary-focus disabled:opacity-50 disabled:pointer-events-none",
};

// Größen: md = Preline „Small“ (buttons-sizes), xs/sm/lg = Pro cat-buttons (xs py-1.5 px-3 text-xs, sm py-1.5 px-3, lg py-2.5 px-4).
const GROESSE: Record<KnopfGroesse, string> = {
  xs: "py-1.5 px-3 inline-flex items-center gap-x-2 text-xs font-medium rounded-lg",
  sm: "py-1.5 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg",
  md: "py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg",
  lg: "py-2.5 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg",
  "mobil-gross": "py-3 px-4 lg:py-2 lg:px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg",
  touch: "py-3 px-4 min-h-11 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg",
};

// Textknopf (Preline Link): ohne Polster, `font-semibold`; die Größe wird ignoriert.
const TEXT_GROESSE = "inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg";
// Kleiner Textknopf (W206 G4, Betreiber 15.10.2026, Vorschau gv-aufgabe4 v1-reiter-bezuege): Aktion im Kartenkopf `text-xs`, normale Stärke, Symbol
// `size-3.5`; nur bei `variante="text"` + `groesse="xs"` (vorher ignorierte der Textknopf die Größe; einziger Bestandsverbraucher mit xs auf md umgestellt).
const TEXT_GROESSE_XS = "inline-flex items-center gap-x-1 text-xs font-normal rounded-lg";

// Nur-Symbol: Preline icon-only (quadratisch, zentriert), Pro ghost-icon: xs 30 px, sm 34 px, md 38 px, lg 42 px. Beschriftung Pflicht.
const NUR_SYMBOL: Record<KnopfGroesse, string> = {
  xs: "size-7.5 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg",
  sm: "size-8.5 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg",
  md: "size-9.5 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg",
  lg: "size-10.5 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg",
  "mobil-gross": "size-11 lg:size-9.5 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg",
  touch: "size-11 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg",
};

const KI = "ai-gated ai-mark";

/*
 * KI-Knopf, Betreiber-Entscheid W183 „A + B“ (14.10.2026, AW-076-Nachtrag):
 * A: Ein KI-Auslöser trägt IMMER das Symbol Sparkles (Lucide-Pfad inline, das Paket bindet `lucide-react` nicht selbst ein), wenn der
 *    Aufrufer kein eigenes `symbol` setzt. Das Symbol ist violett (ki-600), der Knopftext bleibt neutral.
 * B: Voll-Knopf (`sekundaer`) mit `ki` = Segment-Form wie `VerweisChip`: EIN Link bzw. Button, links ein Segment (ki-50, Trennlinie
 *    ki-200, Symbol ki-600), rechts der Text neutral auf dem normalen Knopfgrund. Hover/Fokus tönt beide Hälften (`group`).
 *    Alle Ketten fest; die ki-Klassen stehen nur in den `KI_SEGMENT_*`-Konstanten (Gegen-Test ki-violett-gegentest).
 *    Textknopf, Primär/Gefahr/Ghost und ein eigenes `symbol` (Sonderform, z. B. Avatar der Tour) behalten die bisherige Form.
 */
const KI_SEGMENT_AUSSEN = "border-ki-200";
const KI_SEGMENT_KOPF_FARBE = "border-e border-ki-200 bg-ki-50 text-ki-600 group-hover:bg-ki-100 group-focus:bg-ki-100";
const SEGMENT_AUSSEN_FORM = "group inline-flex items-stretch overflow-hidden font-medium rounded-lg border bg-layer shadow-2xs focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none";
const SEGMENT_TEXT_FARBE = "grow items-center gap-x-2 text-layer-foreground group-hover:bg-layer-hover group-focus:bg-layer-focus";
const SEGMENT_TEXT_MITTE = "justify-center";
const SEGMENT_TEXT_LINKS = "justify-start";
const SEGMENT_SCHRIFT: Record<KnopfGroesse, string> = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-sm",
  lg: "text-sm",
  "mobil-gross": "text-sm",
  touch: "text-sm",
};
const SEGMENT_KOPF_POLSTER: Record<KnopfGroesse, string> = {
  xs: "inline-flex items-center py-1.5 px-2.5",
  sm: "inline-flex items-center py-1.5 px-2.5",
  md: "inline-flex items-center py-2 px-2.5",
  lg: "inline-flex items-center py-2.5 px-3",
  "mobil-gross": "inline-flex items-center py-3 px-3 lg:py-2 lg:px-2.5",
  touch: "inline-flex items-center py-3 px-3",
};
const SEGMENT_TEXT_POLSTER: Record<KnopfGroesse, string> = {
  xs: "inline-flex py-1.5 px-3",
  sm: "inline-flex py-1.5 px-3",
  md: "inline-flex py-2 px-3",
  lg: "inline-flex py-2.5 px-4",
  "mobil-gross": "inline-flex py-3 px-4 lg:py-2 lg:px-3",
  touch: "inline-flex py-3 px-4",
};
const KI_SYMBOL_GROESSE: Record<KnopfGroesse, string> = {
  xs: "shrink-0 size-3.5",
  sm: "shrink-0 size-4",
  md: "shrink-0 size-4",
  lg: "shrink-0 size-4",
  "mobil-gross": "shrink-0 size-4",
  touch: "shrink-0 size-4",
};

/** Lucide `Sparkles` (KI-Kennzeichnung, R-F) als Inline-SVG; Farbe erbt (`currentColor`), die Kette setzt der Knopf. */
function KnopfKiSymbol({ className }: { className: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  );
}

// Geschlossene Zusätze (je eine ganze Kette).
const VOLLE_BREITE_MOBIL = "w-full sm:w-auto justify-center";
const VOLLE_BREITE_IMMER = "w-full justify-center";
const VOLLE_BREITE_LINKS = "w-full justify-start";
const UEBER_ZEILENLINK = "relative z-10";
const NUR_AB_MD = "max-md:hidden";
const NUR_BIS_MD = "md:hidden";
const STARR = "shrink-0";
// Link-Stil (P-C 4, Betreiber 03.11.2026 abends, Vorschau C1): unterstrichener Textknopf, nur bei `variante="text"`.
const UNTERSTRICHEN = "underline underline-offset-2";
// Split-Knopf (Preline `split-send-button-with-menu`): die Rundung der inneren Seite entfällt, die rechte Hälfte überlappt den Rand.
const TEIL_RUNDUNG = { start: "rounded-s-lg rounded-e-none", ende: "rounded-e-lg rounded-s-none -ms-px" } as const;

// Preline buttons-loading: Spinner vor dem Text (abgeleitet; `rounded-full` statt `rounded-[999px]`, gleiche Wirkung).
const SPINNER = "animate-spin motion-reduce:animate-none inline-block size-4 border-3 border-current border-t-transparent rounded-full";

/**
 * Durchgereichte `data-*`-Attribute (Anker für Touren, Fokus-Suche, Preline-Overlays). Nur Schlüssel `data-…`; der Baustein
 * filtert zur Laufzeit alles andere heraus (kein Weg für `class`, `style`, `on…` oder beliebige Attribute, r102).
 */
export type KnopfDaten = { [K in `data-${string}`]?: string | true };

type Gemeinsam = {
  /** Technische `data-*`-Attribute (Betreiber-Entscheid Welle 2b, 10.10.2026): nur `data-…`, keine Gestaltung. */
  daten?: KnopfDaten;
  /** `aria-expanded` (Aufklapp-Auslöser, Menü-/Dialog-Auslöser). */
  ariaExpanded?: boolean;
  /** `aria-controls`: Id des gesteuerten Bereichs. */
  ariaControls?: string;
  /** `aria-haspopup`: Art des geöffneten Elements. */
  ariaHaspopup?: "dialog" | "menu" | "listbox";
  /** `aria-pressed` (Umschalter). */
  ariaPressed?: boolean;
  /** `aria-describedby`: Id des beschreibenden Textes (Hilfetext). */
  ariaDescribedby?: string;
  variante?: KnopfVariante;
  /**
   * KI-Auslöser (R-D): setzt `ai-gated ai-mark` und IMMER das Symbol Sparkles (ohne eigenes `symbol`). Als Voll-Knopf
   * (`sekundaer`, mit Text) in Segment-Form (W183 A + B): links Symbol auf ki-50, rechts der Text neutral.
   */
  ki?: boolean;
  /**
   * `true`: `w-full sm:w-auto` (Modal-Fuß); `"immer"`: `w-full` (zentriert); `"links"`: `w-full justify-start`
   * (linksbündige Zeile, z. B. „Aufgabe hinzufügen“ am Brett-Spaltenfuß; Betreiber 11.10.2026, Variante A).
   */
  volleBreite?: boolean | "immer" | "links";
  /** Knopf neben einem Zeilenlink, der die ganze Zeile deckt: `relative z-10`. */
  ueberZeilenlink?: boolean;
  /** Nur ab `md` sichtbar (`max-md:hidden`) bzw. nur darunter (`md:hidden`). */
  sichtbar?: "ab-md" | "bis-md";
  /** In einer Flex-Zeile nicht schrumpfen (`shrink-0`). */
  starr?: boolean;
  /** Link-Stil: Text unterstrichen. Wirkt nur bei `variante="text"` (sonst ignoriert). */
  unterstrichen?: boolean;
  /** Tooltip (`title`). */
  titel?: string;
  /**
   * Hälfte eines geteilten Knopfes (Split-Knopf, Paket E E-5, Betreiber 21.10.2026, Abweichung AW-151): `start` = Hauptknopf links (rechte Ecken
   * eckig), `ende` = Auslöser des Menüs rechts (linke Ecken eckig, `-ms-px` überlappt den Rand). Nur in Paaren in einer `inline-flex`-Hülle.
   */
  teil?: "start" | "ende";
};

type MitText = Gemeinsam & {
  /** `text` ignoriert die Größe (kein Polster). */
  groesse?: KnopfGroesse;
  /** Lucide-Symbol vor dem Text (Aufrufer setzt `shrink-0 size-4 aria-hidden`). */
  symbol?: ReactNode;
  children: ReactNode;
  /**
   * Zugänglicher Name zusätzlich zum sichtbaren Text (`aria-label`), wenn er länger sein soll als die Beschriftung
   * („Frag dazu“ → „Frag dazu: Assistent zu den Verträgen fragen“). Beginnt mit dem sichtbaren Text (WCAG 2.5.3).
   */
  ariaLabel?: string;
  nurSymbol?: undefined;
  label?: undefined;
};

type KnopfSchalter = MitText & {
  href?: undefined;
  extern?: undefined;
  type?: "button" | "submit";
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  /** Vorgang läuft: `disabled` + `aria-busy` + Preline-Spinner (ersetzt das Symbol). */
  laedt?: boolean;
  /** Ref auf das `<button>` (React 19: `ref` als Prop), z. B. für Fokus-Rückgabe. */
  ref?: Ref<HTMLButtonElement>;
};

type KnopfLink = MitText & {
  href: string;
  /** `true`: `<a target="_blank" rel="noopener noreferrer">` statt next/link. */
  extern?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  type?: undefined;
  disabled?: never;
  laedt?: never;
  ref?: Ref<HTMLAnchorElement>;
};

/** Nur Symbol: `label` (→ `aria-label`) ist Pflicht. Größen: xs 30, sm 34, md 38 (Standard), lg 42 px. */
type NurSymbolGemeinsam = Gemeinsam & {
  nurSymbol: true;
  label: string;
  symbol: ReactNode;
  children?: undefined;
  ariaLabel?: undefined;
  groesse?: KnopfGroesse;
};

type KnopfNurSymbol = NurSymbolGemeinsam & {
  href?: undefined;
  extern?: undefined;
  type?: "button" | "submit";
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  laedt?: boolean;
  ref?: Ref<HTMLButtonElement>;
};

/** Symbol-Link in Knopfgestalt (z. B. „Als Seite öffnen“ nur mit Symbol): `aria-label` aus `label`, kein `disabled`/`laedt`. */
type KnopfNurSymbolLink = NurSymbolGemeinsam & {
  href: string;
  extern?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  type?: undefined;
  disabled?: never;
  laedt?: never;
  ref?: Ref<HTMLAnchorElement>;
};

export type KnopfProps = KnopfSchalter | KnopfLink | KnopfNurSymbol | KnopfNurSymbolLink;

/** Lässt nur `data-*`-Schlüssel durch (Laufzeitschutz zusätzlich zum Typ). */
function nurDaten(daten: KnopfDaten | undefined): Record<string, string | true> {
  const aus: Record<string, string | true> = {};
  for (const [k, v] of Object.entries(daten ?? {})) {
    if (k.startsWith("data-") && v !== undefined) aus[k] = v;
  }
  return aus;
}

/**
 * Klassenkette des Menü-Auslösers (rechte Hälfte) eines Split-Knopfes: gleiche Farbe und Größe wie die linke Hälfte (`<Knopf teil="start">`),
 * linke Ecken eckig. Für `DropdownMenue.ausloeserKlasse`, das den Auslöser selbst als `<button>` rendert (Abweichung AW-151).
 */
export function splitAusloeserKlasse(variante: KnopfVariante = "primaer", groesse: KnopfGroesse = "md"): string {
  return cx(GROESSE[groesse], FARBE[variante]).replace(/\s+/g, " ").trim().replace("rounded-lg", TEIL_RUNDUNG.ende);
}

/**
 * Klassenkette eines Nur-Symbol-Auslösers in Knopfgestalt für `DropdownMenue.ausloeserKlasse` (der Baustein rendert den Auslöser selbst als
 * `<button>`): gleiche Maße und Farben wie `<Knopf nurSymbol variante groesse>`, damit ein Menü-Auslöser neben Symbolknöpfen nicht abweicht.
 */
export function symbolAusloeserKlasse(variante: KnopfVariante = "ghost", groesse: KnopfGroesse = "xs"): string {
  return cx(NUR_SYMBOL[groesse], FARBE[variante]).replace(/\s+/g, " ").trim();
}

export function Knopf(props: KnopfProps) {
  const variante = props.variante ?? "sekundaer";
  const groesse = props.groesse ?? "md";
  const form = props.nurSymbol ? NUR_SYMBOL[groesse] : variante === "text" ? (groesse === "xs" ? TEXT_GROESSE_XS : TEXT_GROESSE) : GROESSE[groesse];
  const breite =
    props.volleBreite === "links"
      ? VOLLE_BREITE_LINKS
      : props.volleBreite === "immer"
        ? VOLLE_BREITE_IMMER
        : props.volleBreite
          ? VOLLE_BREITE_MOBIL
          : "";
  // W183 B: Segment-Form nur für den Voll-Knopf mit Text, ohne eigenes Symbol (Sonderformen wie der Tour-Avatar bleiben).
  const segment = Boolean(props.ki && !props.nurSymbol && variante === "sekundaer" && props.symbol === undefined);
  const gemeinsamKette = [
    props.ki ? KI : "",
    props.ueberZeilenlink ? UEBER_ZEILENLINK : "",
    props.sichtbar === "ab-md" ? NUR_AB_MD : props.sichtbar === "bis-md" ? NUR_BIS_MD : "",
    props.starr ? STARR : "",
    props.unterstrichen && variante === "text" && !props.nurSymbol ? UNTERSTRICHEN : "",
    breite,
  ];
  const kette = (
    segment
      ? cx(...gemeinsamKette, SEGMENT_AUSSEN_FORM, SEGMENT_SCHRIFT[groesse], KI_SEGMENT_AUSSEN)
      : cx(...gemeinsamKette, form, FARBE[variante])
  )
    .replace(/\s+/g, " ")
    .trim()
    .replace("rounded-lg", props.teil && !segment ? TEIL_RUNDUNG[props.teil] : "rounded-lg");
  const segmentKopf = cx(SEGMENT_KOPF_POLSTER[groesse], KI_SEGMENT_KOPF_FARBE).replace(/\s+/g, " ").trim();
  const segmentText = cx(SEGMENT_TEXT_POLSTER[groesse], SEGMENT_TEXT_FARBE, props.volleBreite === "links" ? SEGMENT_TEXT_LINKS : SEGMENT_TEXT_MITTE)
    .replace(/\s+/g, " ")
    .trim();

  // W104 A (AW-076): bei KI-Auslösern ist nur das Symbol violett (`text-ki`), der Knopftext bleibt neutral (CQ2 a).
  // W183 A: KI-Auslöser ohne eigenes Symbol bekommen Sparkles (Pflichtsymbol, R-D/R-F).
  const kiSymbol = <KnopfKiSymbol className={KI_SYMBOL_GROESSE[groesse]} />;
  const symbolKnoten = props.ki ? <span className="flex shrink-0 text-ki">{props.symbol ?? kiSymbol}</span> : props.symbol;
  // Segment: Symbol (bzw. Spinner bei `laedt`) im linken Segment, der Text im rechten.
  const segmentInhalt = (laedt: boolean) => (
    <>
      <span className={segmentKopf}>{laedt ? <span className={SPINNER} aria-hidden="true" /> : kiSymbol}</span>
      <span className={segmentText}>{props.children}</span>
    </>
  );

  const daten = nurDaten(props.daten);
  const aria = {
    "aria-expanded": props.ariaExpanded,
    "aria-controls": props.ariaControls,
    "aria-haspopup": props.ariaHaspopup,
    "aria-pressed": props.ariaPressed,
    "aria-describedby": props.ariaDescribedby,
  };

  if (props.href !== undefined) {
    const inhalt = segment ? (
      segmentInhalt(false)
    ) : (
      <>
        {symbolKnoten}
        {props.children}
      </>
    );
    return (
      <>
        {/* Preline: component/buttons/buttons-white-color-variants */}
        {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-badge-styles/badge-with-divider-segment (abgeleitet, Form wie lese-verweis-chip) */}
        {props.extern ? (
          <a
            ref={props.ref}
            className={kette}
            href={props.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={props.onClick}
            title={props.titel}
            aria-label={props.nurSymbol ? props.label : props.ariaLabel}
            {...aria}
            {...daten}
          >
            {inhalt}
          </a>
        ) : (
          <Link
            ref={props.ref}
            className={kette}
            href={props.href}
            onClick={props.onClick}
            title={props.titel}
            aria-label={props.nurSymbol ? props.label : props.ariaLabel}
            {...aria}
            {...daten}
          >
            {inhalt}
          </Link>
        )}
      </>
    );
  }

  const gesperrt = Boolean(props.disabled) || Boolean(props.laedt);
  return (
    <>
      {/* Preline: component/buttons/buttons-types */}
      <button
        ref={props.ref}
        type={props.type ?? "button"}
        className={kette}
        disabled={gesperrt}
        aria-busy={props.laedt ? true : undefined}
        aria-label={props.nurSymbol ? props.label : props.ariaLabel}
        title={props.titel}
        onClick={props.onClick}
        {...aria}
        {...daten}
      >
        {/* Preline: component/buttons/buttons-loading */}
        {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-badge-styles/badge-with-divider-segment (abgeleitet, Form wie lese-verweis-chip) */}
        {segment ? (
          segmentInhalt(Boolean(props.laedt))
        ) : (
          <>
            {props.laedt ? <span className={SPINNER} aria-hidden="true" /> : symbolKnoten}
            {props.nurSymbol ? null : props.children}
          </>
        )}
      </button>
    </>
  );
}
